import { Loading } from "../app/Status";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useId, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import {
  PLANNING_KIND_LABELS,
  PLANNING_KINDS,
  planningPeriod,
  shiftAnchor,
  todayIso,
  type PlanningKind,
  type PlanningResponse,
  type PlanningView,
} from "@klantpunt/shared";
import { Segmented } from "../app/Choice";
import { IconChevronLeft, IconChevronRight, IconFilter, IconPlus } from "../app/icons";
import { Modal } from "../app/Modal";
import { PageHeader } from "../app/PageHeader";
import { AddLineDialog, useAddModes } from "../features/planning/AddLineDialog";
import { PlanningAgenda } from "../features/planning/PlanningAgenda";
import { PlanningGrid } from "../features/planning/PlanningGrid";
import { moveBody, PlanningLineDialog, useAppointments } from "../features/planning/PlanningLineDialog";
import { PlanningMark } from "../features/planning/PlanningMark";
import {
  activeFilterCount,
  applyFilters,
  counts,
  groupRows,
  NO_FILTERS,
  appointmentText,
  performerKey,
  toRows,
  visibleRows,
  type Group,
  type PlanFilters,
  type PlanRow,
  type StatusFilter,
} from "../features/planning/planningView";
import { api, ApiError } from "../lib/api";
import { hasModule, useMe } from "../lib/auth";
import { useBuildingContext, useBuildings } from "../lib/buildingContext";
import { useClientBuildingFilter } from "../lib/clientContext";
import { useToast } from "../lib/toast";
import { PHONE_QUERY, useMediaQuery } from "../lib/useMediaQuery";

const VIEWS: PlanningView[] = ["jaar", "maand", "week"];
const VIEW_LABELS: Record<PlanningView, string> = { jaar: "Jaar", maand: "Maand", week: "Week" };
const isView = (v: string | null): v is PlanningView => VIEWS.includes(v as PlanningView);
const isDate = (v: string | null): v is string => Boolean(v && /^\d{4}-\d{2}-\d{2}$/.test(v));

/**
 * Operationele planning (kruisjesplanning, stap 2 en 3): keuringen, onderhoud, taken en meterrondes over alle
 * gebouwen per jaar, maand of week. Op de telefoon een agenda per week met verlopen werk bovenaan. Een regel
 * openen = inplannen, verplaatsen of afronden; op desktop sleep je een afspraak naar een andere dag of week.
 */
export function PlanningPage() {
  const { data: me } = useMe();
  const phone = useMediaQuery(PHONE_QUERY);
  const [params, setParams] = useSearchParams();
  const today = todayIso();
  const view: PlanningView = isView(params.get("weergave"))
    ? (params.get("weergave") as PlanningView)
    : phone
      ? "week"
      : "jaar";
  const anchor = isDate(params.get("datum")) ? params.get("datum")! : today;
  const period = useMemo(() => planningPeriod(view, anchor), [view, anchor]);
  const go = (v: PlanningView, a: string) =>
    setParams(
      (p) => {
        p.set("weergave", v);
        if (a === today) p.delete("datum");
        else p.set("datum", a);
        return p;
      },
      { replace: true },
    );

  // Het gebouw volgt de gebouwkiezer in de bovenbalk (zoals de MJOP); kies je er hier een, dan geldt dat ook daar.
  const ctx = useBuildingContext();
  const [filters, setFilters] = useState<PlanFilters>(NO_FILTERS);
  const f: PlanFilters = { ...filters, buildingId: ctx.id ?? "" };
  const setF = (next: PlanFilters) => {
    if (next.buildingId !== f.buildingId) ctx.setBuilding(next.buildingId || null);
    setFilters(next);
  };
  const [status, setStatus] = useState<StatusFilter>("");
  const [group, setGroup] = useState<Group>("gebouw");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersId = useId();
  const [openId, setOpenId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const addModes = useAddModes();
  const appointments = useAppointments();
  const toast = useToast();

  const allowed = hasModule(me, "assets") || hasModule(me, "issues") || hasModule(me, "meters");
  const q = useQuery({
    queryKey: ["planning", period.from, period.to],
    queryFn: () => api<PlanningResponse>(`/planning?from=${period.from}&to=${period.to}`),
    placeholderData: keepPreviousData,
    enabled: allowed,
  });
  const ofClient = useClientBuildingFilter();
  const buildings = (useBuildings().data ?? []).filter((b) => ofClient(b.id));

  const all = useMemo(
    () => (q.data ? toRows(q.data.lines, period.from, period.to, today) : []),
    [q.data, period.from, period.to, today],
  );
  const filtered = applyFilters(all, f, ofClient);
  const n = counts(filtered);
  const shown = visibleRows(filtered, status);
  const groups = groupRows(shown, group);
  const performers = [...new Map(all.map((r) => [performerKey(r.l), r.l.performer?.name])).entries()].sort(
    ([a], [b]) => (a === "-" ? 1 : b === "-" ? -1 : a.localeCompare(b, "nl")),
  );
  const kinds = PLANNING_KINDS.filter((k) => all.some((r) => r.l.kind === k));
  const open = openId ? all.find((r) => r.l.id === openId) : undefined;

  /** Na slepen: meteen opslaan, met de mogelijkheid om terug te zetten. */
  async function move(r: PlanRow, startOn: string) {
    const a = r.l.appointment;
    if (!a || startOn === a.startOn) return;
    try {
      const next = await appointments.save(r.l, moveBody(a, startOn));
      const late = r.l.dueOn && next.endOn > r.l.dueOn ? ", na de vervaldatum" : "";
      toast(`${r.l.title} verplaatst naar ${appointmentText(next)}${late}`, {
        action: {
          label: "Ongedaan maken",
          onClick: () => void appointments.save({ ...r.l, appointment: next }, moveBody(next, a.startOn)),
        },
      });
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Verplaatsen lukte niet");
    }
  }

  if (!allowed)
    return (
      <div className="center">
        <p>Geen toegang tot de planning.</p>
      </div>
    );

  const filterControls = (
    <>
      <div className="filters">
        <select aria-label="Gebouw" value={f.buildingId} onChange={(e) => setF({ ...f, buildingId: e.target.value })}>
          <option value="">Alle gebouwen</option>
          {buildings.map((b) => (
            <option key={b.id} value={b.id}>
              {b.code} {b.name}
            </option>
          ))}
        </select>
        <select aria-label="Uitvoerder" value={f.performer} onChange={(e) => setF({ ...f, performer: e.target.value })}>
          <option value="">Alle uitvoerders</option>
          {performers.map(([k, name]) => (
            <option key={k} value={k}>
              {name || "Zonder uitvoerder"}
            </option>
          ))}
        </select>
        <select
          aria-label="Soort"
          value={f.kind}
          onChange={(e) => setF({ ...f, kind: e.target.value as PlanningKind | "" })}
        >
          <option value="">Alle soorten</option>
          {kinds.map((k) => (
            <option key={k} value={k}>
              {PLANNING_KIND_LABELS[k]}
            </option>
          ))}
        </select>
      </div>
      {activeFilterCount(f) > 0 && (
        <button type="button" className="btn small" onClick={() => setF({ ...NO_FILTERS, q: f.q })}>
          Filters wissen
        </button>
      )}
    </>
  );
  const kpi = (s: StatusFilter, label: string, count: number, dot?: string) => (
    <button type="button" className="kpi" aria-pressed={status === s} onClick={() => setStatus(status === s ? "" : s)}>
      {dot && <i className={`obdot ${dot}`} aria-hidden="true" />}
      {label} <b className="num">{count}</b>
    </button>
  );

  return (
    <div className="page planning">
      <PageHeader
        label="Planning"
        title="Planning"
        action={
          addModes.length > 0 && (
            <button type="button" className="btn primary" onClick={() => setAdding(true)}>
              <IconPlus aria-hidden="true" /> Toevoegen
            </button>
          )
        }
      />
      <div className="kp-bar">
        <Segmented<PlanningView>
          label="Weergave"
          value={view}
          onChange={(v) => go(v, anchor)}
          items={VIEWS.map((v) => ({ value: v, label: VIEW_LABELS[v] }))}
        />
        <div className="kp-nav">
          <button
            type="button"
            className="btn small ico"
            aria-label="Vorige periode"
            title="Vorige periode"
            onClick={() => go(view, shiftAnchor(view, anchor, -1))}
          >
            <IconChevronLeft size={16} aria-hidden="true" />
          </button>
          <button type="button" className="btn small" onClick={() => go(view, today)}>
            Vandaag
          </button>
          <button
            type="button"
            className="btn small ico"
            aria-label="Volgende periode"
            title="Volgende periode"
            onClick={() => go(view, shiftAnchor(view, anchor, 1))}
          >
            <IconChevronRight size={16} aria-hidden="true" />
          </button>
        </div>
        <h2 className="kp-period" aria-live="polite">
          {period.title}
        </h2>
      </div>
      <div className="obcounts" role="group" aria-label="Filter op status">
        {kpi("verlopen", "Verlopen", n.verlopen, "ob-overdue")}
        {kpi("na_vervaldatum", "Gepland na vervaldatum", n.na_vervaldatum, "ob-overdue")}
        {kpi("te_plannen", "Te plannen", n.te_plannen, "ob-soon")}
        {kpi("binnenkort", "Komende 4 weken", n.binnenkort)}
      </div>
      <div className="kp-filters">
        <input
          type="search"
          placeholder="Zoek regel, asset of uitvoerder"
          aria-label="Zoeken"
          value={f.q}
          onChange={(e) => setF({ ...f, q: e.target.value })}
        />
        {!phone && (
          <select aria-label="Groeperen" value={group} onChange={(e) => setGroup(e.target.value as Group)}>
            <option value="gebouw">Per gebouw</option>
            <option value="uitvoerder">Per uitvoerder</option>
            <option value="soort">Per soort</option>
          </select>
        )}
        <button
          type="button"
          className="btn small"
          aria-expanded={filtersOpen}
          aria-controls={filtersOpen ? filtersId : undefined}
          onClick={() => setFiltersOpen(!filtersOpen)}
        >
          <IconFilter size={16} aria-hidden="true" />
          {activeFilterCount(f) ? `Filters (${activeFilterCount(f)})` : "Filters"}
        </button>
      </div>
      {filtersOpen &&
        (phone ? (
          <Modal id={filtersId} title="Filters" onClose={() => setFiltersOpen(false)}>
            <div className="filtersheet">{filterControls}</div>
          </Modal>
        ) : (
          <div className="kp-filterpanel" id={filtersId}>
            {filterControls}
          </div>
        ))}
      {!phone && (
        <p className="kp-legend small muted">
          <span>
            <PlanningMark type="due" status="later" dates={[]} /> Volgende keer
          </span>
          <span>
            <PlanningMark type="due" status="te_plannen" dates={[]} /> Te plannen
          </span>
          <span>
            <PlanningMark type="due" status="verlopen" dates={[]} /> Verlopen
          </span>
          <span>
            <PlanningMark type="forecast" status="later" dates={[]} /> Prognose
          </span>
          <span>
            <PlanningMark type="done" status="later" dates={[]} /> Uitgevoerd
          </span>
          <span>
            <i className="kp-blk kp-blk-ok kp-blk-s kp-blk-e" aria-hidden="true" /> Gepland, bevestigd
          </span>
          <span>
            <i className="kp-blk kp-blk-tent kp-blk-s kp-blk-e" aria-hidden="true" /> Gepland, voorlopig
          </span>
        </p>
      )}
      <div aria-busy={q.isFetching}>
        {q.isPending ? (
          <Loading />
        ) : q.isError ? (
          <p className="muted">De planning kon niet geladen worden.</p>
        ) : !all.length ? (
          <p className="muted">
            Nog niets te plannen. Voeg bij een asset een keuring of onderhoud toe, of geef een taak een uiterste datum.
          </p>
        ) : !shown.length ? (
          <p className="muted">Niets in deze selectie.</p>
        ) : phone ? (
          <PlanningAgenda rows={shown} onOpen={(r) => setOpenId(r.l.id)} />
        ) : (
          <PlanningGrid
            period={period}
            groups={groups}
            today={today}
            groupBy={group}
            onOpen={(r) => setOpenId(r.l.id)}
            onMove={(r, startOn) => void move(r, startOn)}
          />
        )}
      </div>
      {open && <PlanningLineDialog key={open.l.id} row={open} rows={all} onClose={() => setOpenId(null)} />}
      {adding && (
        <AddLineDialog
          buildings={buildings}
          buildingId={ctx.id}
          onClose={() => setAdding(false)}
          onAdded={(lineId, plan) => {
            setAdding(false);
            if (plan) setOpenId(lineId);
          }}
        />
      )}
    </div>
  );
}
